/**
 * Stats Data Mapper
 * Transforms API responses to chart-ready formats
 * Maintains consistency with existing chart components
 */

/**
 * Map API response to KPI cards format
 * Handles both new API format and legacy format
 */
export const mapKPIData = (apiData) => {
  // Support both new API format and legacy format
  const overview = apiData?.overview || apiData?.data?.overview || {};
  const deltas = apiData?.deltas || apiData?.data?.deltas || {};

  return [
    {
      title: 'Total Loads',
      value: formatNumber(overview.totalLoads || 0),
      trend: {
        type: deltas.totalLoads >= 0 ? 'positive' : 'negative',
        percentage: Math.abs(deltas.totalLoads || 0),
        text: 'vs last period'
      },
      variant: 'white',
      diagramType: 'progress',
      diagramData: {
        value: calculateDeliveryRate(overview),
        label: `${calculateDeliveryRate(overview)}%`
      },
      icon: 'statup'
    },
    {
      title: 'Gross Revenue',
      value: formatCurrency(overview.revenue || 0),
      trend: {
        type: deltas.revenue >= 0 ? 'positive' : 'negative',
        percentage: Math.abs(deltas.revenue || 0),
        text: 'vs last period'
      },
      variant: 'blue',
      diagramType: 'area',
      diagramData: {
        data: apiData?.series?.moneyTrend?.revenue || []
      },
      icon: 'statup'
    },
    {
      title: 'Total Profit',
      value: formatCurrency(overview.profit || 0),
      trend: {
        type: deltas.profit >= 0 ? 'positive' : 'negative',
        percentage: Math.abs(deltas.profit || 0),
        text: 'vs last period'
      },
      variant: 'white',
      diagramType: 'bar',
      diagramData: {
        data: apiData?.series?.moneyTrend?.profit || []
      },
      icon: 'statup'
    }
  ];
};

/**
 * Map loads by status to donut chart format
 */
export const mapLoadsByStatusDonut = (apiData) => {
  // Support both formats
  const loads = apiData?.loads?.byStatus || apiData?.data?.loads?.byStatus || {};
  const total = apiData?.loads?.total || apiData?.data?.loads?.total || 0;

  const segments = [
    { label: 'Delivered', value: calculatePercent(loads.delivered, total), slug: 'delivered' },
    { label: 'Picked Up', value: calculatePercent(loads.pickedUp, total), slug: 'pickedup' },
    { label: 'Dispatched', value: calculatePercent(loads.dispatched, total), slug: 'dispatched' },
    { label: 'Listed', value: calculatePercent(loads.listed, total), slug: 'listed' },
    { label: 'On Hold', value: calculatePercent(loads.onHold, total), slug: 'onhold' },
    { label: 'Cancelled', value: calculatePercent(loads.cancelled, total), slug: 'cancelled' }
  ].filter(seg => seg.value > 0);

  return {
    title: 'Loads Status',
    totalLabel: 'Total',
    total,
    segments
  };
};

/**
 * Map money trend to line chart format
 */
export const mapMoneyTrendLine = (apiData) => {
  const series = apiData?.series?.moneyTrend || {};
  const categories = apiData?.series?.categories || [];

  // Ensure data arrays are valid
  const revenueData = Array.isArray(series.revenue) ? series.revenue : [];
  const expenseData = Array.isArray(series.expense) ? series.expense : [];
  const profitData = Array.isArray(series.profit) ? series.profit : [];

  // Generate default categories if needed
  const defaultCategories = generateDefaultCategories();
  const validCategories = Array.isArray(categories) && categories.length > 0 
    ? categories 
    : defaultCategories;

  // Build series array - always include at least one series for chart to render
  const seriesArray = [];
  
  if (revenueData.length > 0) {
    seriesArray.push({
      name: 'Revenue',
      slug: 'revenue',
      data: revenueData
    });
  }
  
  if (expenseData.length > 0) {
    seriesArray.push({
      name: 'Expense',
      slug: 'expense',
      data: expenseData
    });
  }
  
  if (profitData.length > 0) {
    seriesArray.push({
      name: 'Profit',
      slug: 'profit',
      data: profitData
    });
  }

  // If no series have data, provide default empty series to prevent errors
  if (seriesArray.length === 0) {
    seriesArray.push({
      name: 'Revenue',
      slug: 'revenue',
      data: new Array(validCategories.length).fill(0)
    });
    seriesArray.push({
      name: 'Expense',
      slug: 'expense',
      data: new Array(validCategories.length).fill(0)
    });
  }

  return {
    title: 'Revenue / Expense / Profit Trend',
    subtitle: 'Daily comparison',
    categories: validCategories,
    series: seriesArray
  };
};

/**
 * Map payment status to donut chart format
 */
export const mapPaymentStatusDonut = (apiData, type = 'receivable') => {
  const payment = apiData?.[type] || {};
  const byStatus = payment.byStatusCounts || {};
  const total = payment.outstanding?.count || 0;

  const segments = [
    { label: 'Paid', value: calculatePercent(byStatus.received || byStatus.paid, total), slug: 'paid' },
    { label: 'Outstanding', value: calculatePercent(byStatus.invoiced || byStatus.pending, total), slug: 'outstanding' },
    { label: 'Overdue', value: calculatePercent(payment.overdue?.count || 0, total), slug: 'overdue' }
  ].filter(seg => seg.value > 0);

  return {
    title: `${type.charAt(0).toUpperCase() + type.slice(1)} Status`,
    totalLabel: 'Total',
    total,
    segments
  };
};

/**
 * Map aging buckets to bar chart format
 */
export const mapAgingBuckets = (apiData, type = 'receivable') => {
  const aging = apiData?.aging?.[type] || {};
  
  return {
    title: `${type.charAt(0).toUpperCase() + type.slice(1)} Aging`,
    legend: [
      { label: '0-7 days', slug: 'b0_7', key: 'b0_7' },
      { label: '8-14 days', slug: 'b8_14', key: 'b8_14' },
      { label: '15-30 days', slug: 'b15_30', key: 'b15_30' },
      { label: '31+ days', slug: 'b31p', key: 'b31p' }
    ],
    data: [
      {
        label: 'Count',
        b0_7: aging.b0_7?.count || 0,
        b8_14: aging.b8_14?.count || 0,
        b15_30: aging.b15_30?.count || 0,
        b31p: aging.b31p?.count || 0
      }
    ]
  };
};

/**
 * Map top lists (customers, carriers, users)
 */
export const mapTopLists = (apiData) => {
  return {
    customers: (apiData?.tops?.customers || []).slice(0, 10),
    carriers: (apiData?.tops?.carriers || []).slice(0, 10),
    users: (apiData?.tops?.users || []).slice(0, 10)
  };
};

// Helper functions
const formatCurrency = (value) => {
  if (typeof value !== 'number') return '$0.00';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatNumber = (value) => {
  if (typeof value !== 'number') return '0';
  return value.toLocaleString('en-US');
};

const calculatePercent = (value, total) => {
  if (!total || total === 0) return 0;
  return Math.round((value / total) * 100);
};

const calculateDeliveryRate = (overview) => {
  const delivered = overview.delivered || 0;
  const total = overview.totalLoads || 0;
  return total > 0 ? Math.round((delivered / total) * 100) : 0;
};

const generateDefaultCategories = () => {
  const today = new Date();
  const categories = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    categories.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
  }
  return categories;
};
